import React, { useEffect, useState } from 'react';
import { SystemSetting } from '../../api/admin';
import { useAdminOperations } from '../../hooks/useAdminOperations';
import Button from '../ui/Button';
import FormInput from '../ui/FormInput';
import Modal from '../ui/Modal';

interface EditSystemSettingModalProps {
  isOpen: boolean;
  setting: SystemSetting | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditSystemSettingModal: React.FC<EditSystemSettingModalProps> = ({ isOpen, setting, onClose, onSuccess }) => {
  const [value, setValue] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { updateSystemSetting, showToast } = useAdminOperations();

  useEffect(() => {
    if (setting) {
      setValue(typeof setting.value === 'boolean' ? setting.value.toString() : String(setting.value));
      setDescription(setting.description || '');
    }
  }, [setting]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!setting) return;

    setIsSubmitting(true);

    try {
      // Parse value based on the original type
      let parsedValue: unknown = value;

      if (typeof setting.value === 'boolean') {
        parsedValue = value.toLowerCase() === 'true';
      } else if (typeof setting.value === 'number') {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          showToast('Invalid number format', 'error');
          setIsSubmitting(false);
          return;
        }
        parsedValue = numValue;
      }

      const result = await updateSystemSetting(setting.key, parsedValue, description);

      if (result) {
        onSuccess();
        onClose();
      }
    } catch {
      console.error('Failed to update system setting');
      // Error is already handled by the hook
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setValue('');
    setDescription('');
    onClose();
  };

  if (!setting) return null;

  const isBoolean = typeof setting.value === 'boolean';
  const isNumber = typeof setting.value === 'number';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit System Setting">
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormInput label="Setting Key" value={setting.key} disabled className="bg-slate-50 text-slate-500" />

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Value</label>
          {isBoolean ? (
            <select
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-700"
              required
            >
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          ) : (
            <FormInput
              type={isNumber ? 'number' : 'text'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={isSubmitting}
              required
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Description (Optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isSubmitting}
            rows={3}
            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-700"
            placeholder="Enter a description for this setting..."
          />
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting} isLoading={isSubmitting}>
            Update Setting
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EditSystemSettingModal;
